package org.vreo.devicehub.features.liveAccess.presentation

import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.sample
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.milliseconds

import androidx.compose.ui.graphics.ImageBitmap
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.liveAccess.domain.LiveAccessRepository
import org.vreo.devicehub.features.liveAccess.domain.WebRtcConnectMode

/**
 * Live access view model, manages state of live-view screen
 *
 * @property authentication user and target device authentication
 * @property repository Live access/streaming repository for accessing live video feed
 */
class LiveAccessViewModel(
    private val authentication: DeviceAuthentication,
    private val repository: LiveAccessRepository
) : ViewModel() {
    private val _state = MutableStateFlow(LiveAccessState(isDeviceActive = authentication.isDeviceActive))
    val state = _state.asStateFlow()

    private val _frame = MutableStateFlow<ImageBitmap?>(null)
    val frame = _frame.asStateFlow()

    private var streamingJob: Job? = null

    fun onAction(action: LiveAccessActions) {
        when (action) {
            is LiveAccessActions.OnClearError -> {
                _state.update {
                    it.copy(error = null)
                }
            }

            is LiveAccessActions.OnUpdateConnectionMode -> {
                updateConnectionMode(action.index)
            }

            is LiveAccessActions.OnConnectClicked -> {
                connectToTargetDevice()
            }

            is LiveAccessActions.OnDisconnectClicked -> {
                disconnectFromTargetDevice()
            }

            is LiveAccessActions.OnEnableClicked -> {
                if (action.enable) {
                    startVideoStream()
                } else {
                    stopVideoStream()
                }
            }
        }
    }

    /**
     * Update connection mode state
     *
     * @param modeIndex Mode index to be updated to
     */
    private fun updateConnectionMode(modeIndex: Int) = viewModelScope.launch {
        val currentState = _state.value
        if (currentState.connectionModeIndex == modeIndex) {
            return@launch
        }

        val wasConnected = currentState.connected

        val job = disconnectFromTargetDevice()
        job.join()

        _state.update { it.copy(connectionModeIndex = modeIndex) }

        if (wasConnected) {
            connectToTargetDevice()
        }
    }

    /**
     * Send web rtc offer to the target/selected device and establish peer-to-peer connection
     */
    private fun connectToTargetDevice() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        val currentState = _state.value

        // Check if device is active
        if (!currentState.isDeviceActive) {
            _state.update {
                it.copy(
                    isLoading = false,
                    connected = false,
                    error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()
                )
            }
            return@launch
        }

        runCatching {
            val res = repository.connect(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                mode = WebRtcConnectMode.entries[currentState.connectionModeIndex],
            )

            when (res) {
                is Result.Success -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            connected = true,
                        )
                    }
                    startVideoStream()
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            connected = false,
                            error = res.error.toUiText(),
                        )
                    }
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    connected = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Disconnect from target device
     */
    private fun disconnectFromTargetDevice() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        runCatching {
            when (val result = repository.disconnect()) {
                is Result.Success -> {
                    streamingJob?.cancel()
                    _frame.value = null
                    _state.update {
                        it.copy(
                            isLoading = false,
                            connected = false,
                            liveViewEnabled = false
                        )
                    }
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = result.error.toUiText(),
                        )
                    }
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Start video stream
     */
    @OptIn(FlowPreview::class)
    private fun startVideoStream() = viewModelScope.launch {
        // Enable live view
        when (val result = repository.enableVideo(true)) {
            is Result.Success -> {
                _state.update {
                    it.copy(liveViewEnabled = true)
                }
            }

            is Result.Error -> {
                _state.update {
                    it.copy(
                        liveViewEnabled = false,
                        error = result.error.toUiText()
                    )
                }
            }
        }

        // Start streaming
        streamingJob?.cancel()
        streamingJob = repository
            .getVideoStream()
            .sample(167.milliseconds)
            .onEach { bitmap ->
                _frame.value = bitmap
            }
            .catch { e ->
                AppLogger.error("Video stream error: ${e.message}")
                _state.update {
                    it.copy(error = DataError.Local.SERIALIZATION.toUiText())
                }
            }
            .launchIn(viewModelScope)
    }

    /**
     * Stop video stream
     */
    private fun stopVideoStream() = viewModelScope.launch {
        // Disable live view
        when (val result = repository.enableVideo(false)) {
            is Result.Success -> {
                _state.update {
                    it.copy(liveViewEnabled = false)
                }
            }

            is Result.Error -> {
                _state.update {
                    it.copy(
                        liveViewEnabled = false,
                        error = result.error.toUiText()
                    )
                }
            }
        }
        streamingJob?.cancel()
        _frame.value = null
    }
}